import { normalizeArabic } from '@palaccount/config';
import { Bell, CircleHelp, Plus, SlidersHorizontal, type LucideIcon } from 'lucide-react';
import * as React from 'react';
import { useTranslation } from 'react-i18next';

import { requirementFor, type RouteRequirement } from '@/app/route-permissions';
import { NAV_GROUPS, PERSONAL_PAGES } from '@/app/shell/nav';
import {
  hasModule,
  holds,
  holdsArea,
  useCapabilities,
  type SessionCapabilities,
} from '@/lib/permissions';

/**
 * THE NAVIGATION INDEX — the search box finds PAGES, not only records (bugs:
 * G-05 "search box must search … page titles ('فاتورة مشتريات' opens Purchase
 * Bills)", UB-02 "search also finds titles ('new sales invoice')").
 *
 * The server's five groups answer "which record"; this answers "which screen".
 * It is built in the browser from the same tables the menu is drawn from —
 * `NAV_GROUPS`, the tab strips' routes, and the editors' `/new` routes — so a
 * screen the product has is a screen the box can reach, and a screen the
 * product lost is one it stops offering. No request is made for it: the
 * dictionary is already in the page, in BOTH languages, and that is what makes
 * the matching language-blind (below).
 *
 * ── MATCHING IS LANGUAGE-BLIND AND SPELLING-TOLERANT ───────────────────────
 *
 * Every entry is matched against its English AND Arabic labels at once,
 * whatever language the interface is drawn in: an Arabic reader working in the
 * English interface types "فاتورة مشتريات" and still lands on Purchase
 * Invoices. Both sides go through `normalizeArabic`, so a hamza, a ta marbuta
 * or a vowel mark typed differently from the dictionary is not a miss (G-06).
 *
 * `aliasKeys` are matched but never drawn. The Purchase Invoices TAB reads
 * "فواتير الشراء" while the DOCUMENT is "فاتورة مشتريات", and the bug report
 * is written in the document's words — so the document's name is an alias of
 * the tab that lists it.
 *
 * ── PERMISSIONS: THE SAME ANSWER AS THE MENU ───────────────────────────────
 *
 * GEN-12a for records — "must not appear even as a count" — applies to pages
 * unchanged: a page the role may not open is ABSENT from the results, not
 * greyed. The question is asked of `requirementFor(to)`, the exact table the
 * route guard (`guards.tsx` `RequirePermission`) reads, so the box can never
 * offer a page the guard then refuses. BR-PERM-51 still holds: this decides
 * what to DRAW, and the server refuses whatever the browser offered.
 */
export interface NavPage {
  /** The route. Also the row's identity — one entry per destination. */
  readonly to: string;
  /** A screen, or a "new document" action that opens an empty editor. */
  readonly kind: 'page' | 'action';
  /** The label drawn on the row — an existing dictionary key wherever one exists. */
  readonly labelKey: string;
  /** Matched, never drawn (see above). */
  readonly aliasKeys?: readonly string[];
  /**
   * The menu group the page lives in: its label is the row's grey second line
   * and its icon is the row's icon. `null` for the two personal screens that
   * belong to no group (`PERSONAL_PAGES`).
   */
  readonly groupId: string | null;
}

const page = (
  groupId: string,
  to: string,
  labelKey: string,
  aliasKeys?: readonly string[],
): NavPage => ({ to, kind: 'page', labelKey, groupId, ...(aliasKeys ? { aliasKeys } : {}) });

const action = (groupId: string, to: string, labelKey: string): NavPage => ({
  to,
  kind: 'action',
  labelKey,
  groupId,
});

/**
 * Every screen a tab strip or the router offers, in menu order, with the
 * document editors' "New" routes beside the lists they belong to. Keys are the
 * strips' own (`*-tabs.tsx`), so a renamed tab renames its search row.
 */
export const NAV_PAGES: readonly NavPage[] = [
  // Home — the four dashboards (home-tabs.tsx).
  page('home', '/', 'dash.tabFinancial', ['shell.pageFinDash']),
  page('home', '/home/sales', 'dash.tabSales'),
  page('home', '/home/inventory', 'dash.tabInventory'),
  page('home', '/home/cashflow', 'dash.tabCashflow'),

  // Sales (sales-tabs.tsx), each list with the document name it holds.
  page('sales', '/sales', 'sales.tabInvoices', ['search.docInvoice']),
  action('sales', '/sales/new', 'search.newInvoice'),
  page('sales', '/sales/quotes', 'sales.tabQuotes', ['search.docQuotation']),
  action('sales', '/sales/quotes/new', 'search.newQuotation'),
  page('sales', '/sales/orders', 'sales.tabOrders', ['search.docSalesOrder']),
  action('sales', '/sales/orders/new', 'search.newSalesOrder'),
  page('sales', '/sales/deliveries', 'sales.tabDeliveries', ['search.docDeliveryNote']),
  action('sales', '/sales/deliveries/new', 'search.newDeliveryNote'),
  page('sales', '/sales/returns', 'sales.tabReturns', ['search.docCreditNote']),
  action('sales', '/sales/returns/new', 'search.newCreditNote'),

  // Purchases (purchases-tabs.tsx).
  page('purchases', '/purchases', 'po.tabBills', ['search.docPurchaseBill']),
  action('purchases', '/purchases/bills/new', 'search.newPurchaseBill'),
  page('purchases', '/purchases/orders', 'po.tabOrders', ['search.docPurchaseOrder']),
  action('purchases', '/purchases/orders/new', 'search.newPurchaseOrder'),
  page('purchases', '/purchases/receipts', 'po.tabReceipts', ['search.docGoodsReceipt']),
  action('purchases', '/purchases/receipts/new', 'search.newGoodsReceipt'),
  page('purchases', '/purchases/returns', 'po.tabReturns', ['search.docPurchaseReturn']),
  action('purchases', '/purchases/returns/new', 'search.newPurchaseReturn'),
  page('purchases', '/purchases/imports', 'po.tabImports'),
  action('purchases', '/purchases/imports/new', 'imports.newTitle'),

  // Manufacturing (manufacturing-tabs.tsx) — absent with the module (§1.5.3).
  page('manufacturing', '/manufacturing', 'mfg.tabBoms'),
  page('manufacturing', '/manufacturing/work-orders', 'mfg.tabWorkOrders'),
  page('manufacturing', '/manufacturing/planning', 'mfg.tabPlanning'),
  page('manufacturing', '/manufacturing/consumption', 'mfg.tabConsumption'),

  // Inventory & Warehouses (inventory-tabs.tsx).
  page('inventory', '/inventory', 'inv.tabItems'),
  action('inventory', '/inventory/items/new', 'items.newTitle'),
  page('inventory', '/inventory/categories', 'inv.tabCategories'),
  page('inventory', '/inventory/units', 'inv.tabUnits'),
  page('inventory', '/inventory/tiers', 'inv.tabTiers'),
  page('inventory', '/inventory/stock', 'inv.tabStock'),
  page('inventory', '/inventory/movements', 'inv.tabMovements'),
  page('inventory', '/inventory/warehouses', 'inv.tabWarehouses'),
  page('inventory', '/inventory/adjustments', 'inv.tabAdjustments'),
  action('inventory', '/inventory/adjustments/new', 'adj.newTitle'),
  page('inventory', '/inventory/counts', 'inv.tabCounts'),
  action('inventory', '/inventory/counts/new', 'counts.newTitle'),
  page('inventory', '/inventory/transfers', 'inv.tabTransfers'),
  action('inventory', '/inventory/transfers/new', 'trf.newTitle'),

  // Customers, Suppliers & CRM (crm-tabs.tsx).
  page('partners', '/partners', 'crm.tabPartners', ['search.groupPartners']),
  action('partners', '/partners/new', 'partners.newTitle'),
  page('partners', '/partners/groups', 'pgroups.title'),
  page('partners', '/partners/leads', 'crm.tabLeads'),
  page('partners', '/partners/opportunities', 'crm.tabOpportunities'),
  page('partners', '/partners/followups', 'crm.tabFollowups'),

  // Accounting & Finance (accounting-tabs.tsx).
  page('accounting', '/accounting', 'acct.tabReceipts', ['search.docReceipt']),
  action('accounting', '/accounting/receipts/new', 'search.newReceipt'),
  page('accounting', '/accounting/payments', 'acct.tabPayments', ['search.docPaymentVoucher']),
  action('accounting', '/accounting/payments/new', 'search.newPaymentVoucher'),
  page('accounting', '/accounting/journals', 'acct.tabJournals'),
  action('accounting', '/accounting/journals/new', 'search.newJournal'),
  page('accounting', '/accounting/periods', 'acct.tabPeriods'),
  page('accounting', '/accounting/currencies', 'acct.tabCurrencies'),
  page('accounting', '/accounting/assets', 'acct.tabAssets'),
  page('accounting', '/accounting/einvoicing', 'acct.tabEinvoicing'),
  page('accounting', '/accounting/chart', 'acct.tabChart', ['search.groupAccounts']),

  // Banks and Checks (banking-tabs.tsx).
  page('banks', '/banks', 'banking.tabOperations'),
  page('banks', '/banks/accounts', 'banking.tabAccounts'),
  page('banks', '/banks/checks/incoming', 'banking.tabIncoming'),
  page('banks', '/banks/checks/outgoing', 'banking.tabOutgoing'),
  page('banks', '/banks/transfers', 'banking.tabTransfers'),
  action('banks', '/banks/transfers/new', 'search.newCashTransfer'),

  // Employees & HR (hr-tabs.tsx). Payroll is its own area; `requirementFor`
  // names it, so the row follows the tab.
  page('employees', '/employees', 'hr.tabEmployees', ['search.groupEmployees']),
  page('employees', '/employees/roles', 'hr.tabJobRoles'),
  page('employees', '/employees/attendance', 'hr.tabAttendance'),
  page('employees', '/employees/leave', 'hr.tabLeave'),
  page('employees', '/employees/payroll', 'hr.tabPayroll'),
  page('employees', '/employees/recruitment', 'hr.tabRecruitment'),

  // Projects (projects-tabs.tsx) — absent with the module.
  page('projects', '/projects', 'prj.tabProjects'),
  page('projects', '/projects/tasks', 'prj.tabTasks'),
  page('projects', '/projects/timesheets', 'prj.tabTimesheets'),
  page('projects', '/projects/billing', 'prj.tabBilling'),

  // Reports (reports-tabs.tsx). The centres' double gate (`also`) and the
  // Audit Log's own grant are both in `requirementFor`.
  page('reports', '/reports', 'reports.tabSales'),
  page('reports', '/reports/purchases', 'reports.tabPurchases'),
  page('reports', '/reports/inventory', 'reports.tabInventory'),
  page('reports', '/reports/financial', 'reports.tabFinancial'),
  page('reports', '/reports/audit', 'reports.tabAudit'),

  // General Settings and its five sub-screens (router.tsx).
  page('settings', '/settings', 'settings.title'),
  page('settings', '/settings/import-export', 'impexp.title'),
  page('settings', '/settings/integrations', 'integrations.title'),
  page('settings', '/settings/number-sequences', 'numseq.title'),
  page('settings', '/settings/payment-terms', 'payterms.title'),
  page('settings', '/settings/printing', 'printset.title'),

  // Admin (admin-tabs.tsx). Billing is Owner-only (R18), via `requirementFor`.
  page('admin', '/admin', 'admin.tabUsers'),
  page('admin', '/admin/roles', 'admin.tabRoles', ['admin.tabPermissions']),
  page('admin', '/admin/billing', 'billing.title'),

  // The personal screens (nav.ts PERSONAL_PAGES): no group, every session.
  { to: '/notifications', kind: 'page', labelKey: 'notif.title', groupId: null },
  { to: '/personalization', kind: 'page', labelKey: 'personalize.title', groupId: null },
  // UB-03 — the Help page. "help" / "مساعدة" in the search box lands here.
  { to: '/help', kind: 'page', labelKey: 'help.title', aliasKeys: ['help.crumb'], groupId: null },
];

/** The icon a row borrows: its group's, or the personal screen's own. */
export function iconFor(entry: NavPage): LucideIcon {
  if (entry.kind === 'action') return Plus;
  if (entry.groupId === null) {
    if (entry.to === '/notifications') return Bell;
    if (entry.to === '/help') return CircleHelp;
    return SlidersHorizontal;
  }
  return NAV_GROUPS.find((group) => group.id === entry.groupId)?.icon ?? SlidersHorizontal;
}

/** The dictionary key of the row's grey second line — the group, or the personal crumb. */
export function groupLabelKey(entry: NavPage): string {
  if (entry.groupId === null) return PERSONAL_PAGES[entry.to]?.labelKey ?? 'shell.navHome';
  return NAV_GROUPS.find((group) => group.id === entry.groupId)?.labelKey ?? 'shell.navHome';
}

/**
 * The route guard's own question, asked ahead of time — the same clauses in
 * the same order as `RequirePermission` (guards.tsx), so the box and the door
 * cannot disagree: Owner flag, then areas/extras (held-any-of), then `also`
 * (held-all-of), then the module.
 */
export function opens(need: RouteRequirement, caps: SessionCapabilities): boolean {
  if (need.ownerOnly === true && !caps.isOwner) return false;
  const anyOf =
    need.areas.length === 0 && need.extras === undefined
      ? true
      : need.areas.some((area) => holdsArea(caps, area)) ||
        (need.extras?.some((permission) => holds(caps, permission)) ?? false);
  const allOf = need.also?.every((permission) => holds(caps, permission)) ?? true;
  if (!(anyOf && allOf)) return false;
  return need.module === undefined || hasModule(caps, need.module);
}

/** The pages this session may open — GEN-12a's absence, applied to pages. */
export function allowedPages(
  caps: SessionCapabilities,
  pages: readonly NavPage[] = NAV_PAGES,
): readonly NavPage[] {
  // Optimistic while /api/me is in flight, exactly as the guard and the menu
  // are: a page is never hidden from somebody who turns out to hold it.
  if (caps.loading) return pages;
  return pages.filter((entry) => opens(requirementFor(entry.to), caps));
}

/** The two dictionaries, read by key. `i18n.t(key, { lng })` in the app. */
export type LabelOf = (key: string, lng: 'en' | 'ar') => string;

export interface IndexedPage {
  readonly entry: NavPage;
  /** The page's own names — drawn label and aliases — normalised, both languages. */
  readonly names: readonly string[];
  /** Everything matchable — names and the group — joined, normalised. */
  readonly haystack: string;
}

/** Resolves every entry's labels once; the hook memoises it per dictionary. */
export function buildNavIndex(
  labelOf: LabelOf,
  pages: readonly NavPage[] = NAV_PAGES,
): readonly IndexedPage[] {
  return pages.map((entry) => {
    const both = (key: string): string[] => [
      normalizeArabic(labelOf(key, 'en')),
      normalizeArabic(labelOf(key, 'ar')),
    ];
    const names = [entry.labelKey, ...(entry.aliasKeys ?? [])].flatMap(both);
    const group = both(groupLabelKey(entry));
    return { entry, names, haystack: [...names, ...group].join(' ') };
  });
}

/**
 * The matches for a query, best first. Three tiers, mirroring §4.3.2's own
 * ranking for records: a name (label or alias) that STARTS with the query,
 * then anything that CONTAINS it, then one where every typed word appears
 * somewhere ("new invoice" → "New sales invoice"). Ties keep menu order —
 * which is what puts a list before its own "new" action, since the list is
 * written first.
 */
export function matchPages(index: readonly IndexedPage[], query: string): NavPage[] {
  const q = normalizeArabic(query);
  if (q.length === 0) return [];
  const words = q.split(' ');

  const ranked: { entry: NavPage; rank: number; at: number }[] = [];
  index.forEach(({ entry, names, haystack }, at) => {
    let rank: number;
    if (names.some((name) => name.startsWith(q))) rank = 0;
    else if (haystack.includes(q)) rank = 1;
    else if (words.every((word) => haystack.includes(word))) rank = 2;
    else return;
    ranked.push({ entry, rank, at });
  });
  return ranked.sort((a, b) => a.rank - b.rank || a.at - b.at).map((row) => row.entry);
}

/**
 * The pages matching `query` that this session may open. One hook so the
 * panel and the results screen ask the same question.
 */
export function usePageMatches(query: string): NavPage[] {
  const { i18n } = useTranslation();
  const caps = useCapabilities();
  // Rebuilt only when the dictionary changes (a language switch): the labels
  // are the same strings on every keystroke.
  // `i18n.language` is the dependency the instance itself does not express.
  const language = i18n.language;
  const index = React.useMemo(
    () => buildNavIndex((key, lng) => String(i18n.t(key, { lng }))),
    [i18n, language],
  );
  const allowed = new Set(allowedPages(caps).map((entry) => entry.to));
  return matchPages(index, query).filter((entry) => allowed.has(entry.to));
}
